import { Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../store/store';
import { useEffect, useRef } from 'react';
import { toast } from 'sonner';

interface ProtectedAdminRouteProps {
  children: React.ReactNode;
}

/**
 * Guards the admin area. Two very different failure cases used to be collapsed
 * into one: an admin whose token expired got "Page not found" and a bounce to
 * the storefront, which reads as "the app is broken" rather than "please log in
 * again". They're now handled separately:
 *
 *  - not signed in  -> /login, remembering where they were so they land back here
 *  - signed in, not an admin -> storefront, with the deliberately vague message
 *    (we don't confirm the admin area exists to non-admins)
 */
export default function ProtectedAdminRoute({ children }: ProtectedAdminRouteProps) {
  const { user, isAuthenticated } = useSelector((state: RootState) => state.auth);
  const location = useLocation();
  const hasShownToast = useRef(false);

  const isAdmin = user?.roles?.includes('Admin') ?? false;
  const isForbidden = isAuthenticated && !isAdmin;

  useEffect(() => {
    if (isForbidden && !hasShownToast.current) {
      toast.error('Page not found');
      hasShownToast.current = true;
    }
  }, [isForbidden]);

  // Signed out (or the session just expired). sessionMiddleware already showed
  // the "session expired" toast, so don't add another message here.
  if (!isAuthenticated) {
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: `${location.pathname}${location.search}` }}
      />
    );
  }

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}
